import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberMutationReadDataSchema } from '../members/response';
import { escalationMarkHandledOutcomeSchema } from './schema';
import { escalationHandlingDecisionSchema } from './handling-decision';

// ─── mark an escalation handled ─────────────────────────────────────────────
//
// Wire vocabulary for the care team recording that a safety escalation has
// been dealt with. The body is the structured handling decision from
// `handling-decision.ts` rather than being restated here, so the DB CHECKs,
// this contract and the Atlas form cannot drift apart (ADR-066).

/**
 * Body of the staff POST that marks one escalation handled: the whole handling
 * decision. See `escalationHandlingDecisionSchema` for the rules, why it is
 * `.strict()` (ADR-135), and why it never carries the acting clinician or the
 * ASQ result. Note the deliberate asymmetry with the response below — the
 * strictness rule differs by direction, and neither side is an oversight.
 */
export const escalationMarkHandledRequestSchema = escalationHandlingDecisionSchema;
/** What a client sends — the request schema's input shape. */
export type EscalationMarkHandledRequest = z.input<typeof escalationMarkHandledRequestSchema>;

/**
 * What the write returns: the outcome, plus the re-read member projection so
 * the caller has read-your-writes without a second request. Mirrors
 * `healthieLinkRunDataSchema`'s `{ outcome, member }` — including its member
 * schema, which is the WRITE echo and not the read projection.
 *
 * That distinction is load-bearing rather than tidiness. A write echo omits the
 * graduated identity fields so a client bundle whose parser predates them
 * cannot receive a 200 it fails to parse for a mutation it has already applied
 * — which it would then retry. The GET read is the one place a stale parser can
 * fail safely, because a read is retryable and a write is not.
 *
 * Deliberately NOT `.strict()`, unlike that precedent. Atlas parses this
 * through `galapagosHttpClient` → `schema.parse()`, which THROWS on an unknown
 * key rather than stripping it, so a strict schema means a bundle loaded
 * before a deploy that adds a field fails the whole submit rather than
 * ignoring the field it does not know. That is the exact failure ADR-135 Rule
 * 1 removed from `memberReadDataSchema`; repeating it on a new schema in the
 * same feature would reintroduce it.
 */
export const escalationMarkHandledDataSchema = z.object({
  outcome: escalationMarkHandledOutcomeSchema,
  member: memberMutationReadDataSchema,
});
export type EscalationMarkHandledData = z.infer<typeof escalationMarkHandledDataSchema>;

export const escalationMarkHandledResponseSchema = apiEnvelope(escalationMarkHandledDataSchema);
export type EscalationMarkHandledResponse = z.infer<typeof escalationMarkHandledResponseSchema>;
